import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AdminService, DynamoUser, OrgSubscription, SystemLogHealthResponse,
} from '../../core/services/admin.service';

interface KpiStat {
  label: string;
  value: string;
  hint?: string;   // real supporting context (no fabricated deltas)
  icon: string;
}

interface RecentSignup {
  name: string;
  email: string;
  initials: string;
  tier: string;
  joined: string;
}

interface SeverityRow { severity: string; count: number; }

/**
 * Admin overview — real operator summary.
 *
 * All figures are derived from live admin endpoints (users, org subscriptions,
 * and the system-log health aggregate). There are no fabricated numbers and no
 * month-over-month deltas (the platform keeps no historical snapshot to compute
 * them honestly). Revenue/MRR is intentionally omitted — it belongs to billing,
 * which is out of scope.
 */
@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
})
export class OverviewComponent implements OnInit {
  state: 'loading' | 'loaded' | 'error' = 'loading';

  kpis: KpiStat[] = [];
  recentSignups: RecentSignup[] = [];
  health: SystemLogHealthResponse | null = null;
  severityRows: SeverityRow[] = [];

  constructor(private readonly adminService: AdminService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.state = 'loading';
    forkJoin({
      users: this.adminService.listUsers().pipe(map(r => r.users), catchError(() => of([] as DynamoUser[]))),
      subs:  this.adminService.listSubscriptions().pipe(catchError(() => of({ subscriptions: [] as OrgSubscription[], total: 0, nextPageToken: null }))),
      health: this.adminService.getSystemLogHealth().pipe(catchError(() => of(null as SystemLogHealthResponse | null))),
    }).subscribe({
      next: ({ users, subs, health }) => {
        const active = subs.subscriptions.filter(s => s.status === 'ACTIVE').length;
        const monitored = subs.subscriptions.reduce((sum, s) => sum + (s.monitoredServicesCount ?? 0), 0);

        this.kpis = [
          { label: 'Subscriptions',     value: this.fmt(subs.total),        hint: `${active} active`,                    icon: 'credit-card' },
          { label: 'Active',            value: this.fmt(active),            hint: `of ${subs.total}`,                    icon: 'check-circle' },
          { label: 'Monitored services', value: this.fmt(monitored),        hint: `across ${subs.subscriptions.length} orgs`, icon: 'activity' },
          ...(health
            ? [{ label: `Errors (${health.windowHours}h)`, value: this.fmt(health.errorCount), hint: `of ${this.fmt(health.total)} events`, icon: 'zap' }]
            : [{ label: 'Errors', value: '—', hint: 'system logs unavailable', icon: 'zap' }]),
        ];

        this.recentSignups = [...users]
          .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
          .slice(0, 5)
          .map(u => this.toSignup(u));

        this.health = health;
        this.severityRows = health
          ? Object.entries(health.bySeverity)
              .map(([severity, count]) => ({ severity, count }))
              .sort((a, b) => b.count - a.count)
          : [];

        this.state = 'loaded';
      },
      error: () => { this.state = 'error'; },
    });
  }

  private toSignup(u: DynamoUser): RecentSignup {
    const name = `${u.givenName ?? ''} ${u.familyName ?? ''}`.trim() || u.email;
    const initials = ((u.givenName?.[0] ?? '') + (u.familyName?.[0] ?? '')).toUpperCase() || (u.email[0] ?? '?').toUpperCase();
    return {
      name,
      email: u.email,
      initials,
      tier: (u.subscriptionTier ?? 'starter').toLowerCase(),
      joined: u.createdAt,
    };
  }

  private fmt(n: number): string {
    return n.toLocaleString('en-US');
  }

  tierBadge(tier: string): string {
    const map: Record<string, string> = {
      starter: 'bg-zinc-100 text-zinc-600',
      growth: 'bg-blue-100 text-blue-700',
      enterprise: 'bg-violet-100 text-violet-700',
    };
    return map[tier] ?? 'bg-zinc-100 text-zinc-600';
  }

  severityBadge(sev: string): string {
    const s = sev.toUpperCase();
    if (s.includes('ERROR') || s.includes('CRIT') || s.includes('FATAL')) return 'bg-red-100 text-red-700';
    if (s.includes('WARN')) return 'bg-amber-100 text-amber-700';
    return 'bg-zinc-100 text-zinc-600';
  }
}
